import Keycloak from 'keycloak-js';

/**
 * Keycloak configuration for the NEXUS eCMR application
 * 
 * Environment variables (create .env.local file):
 * - REACT_APP_USE_KEYCLOAK: Enable/disable Keycloak authentication (default: false)
 * - REACT_APP_KEYCLOAK_URL: Keycloak server URL (e.g., http://localhost:8080)
 * - REACT_APP_KEYCLOAK_REALM: Keycloak realm name (e.g., nexus-ecmr)
 * - REACT_APP_KEYCLOAK_CLIENT_ID: Keycloak client ID (e.g., nexus-ecmr-web)
 */

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

export const isKeycloakEnabled = (): boolean => {
  return process.env.REACT_APP_USE_KEYCLOAK === 'true';
};

export const getKeycloakConfig = (): KeycloakConfig => {
  return {
    url: process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8080',
    realm: process.env.REACT_APP_KEYCLOAK_REALM || 'nexus-ecmr',
    clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'nexus-ecmr-web',
  };
};

/**
 * Keycloak initialization options
 */
export const keycloakInitOptions = {
  onLoad: 'check-sso' as const, // Check if user is already logged in
  checkLoginIframe: false, // Disable iframe checking for SPA
  pkceMethod: 'S256' as const, // Use PKCE for security
};

/**
 * Token refresh settings
 */
export const tokenRefreshSettings = {
  // Minimum token validity in seconds before refresh (default: 70 seconds)
  minValidity: 70,
  // Token refresh check interval in milliseconds (default: 60 seconds)
  checkInterval: 60000,
};

/**
 * Create and export Keycloak instance
 * Note: At runtime, Keycloak requires 'new', but TypeScript types say it doesn't.
 * We cast to 'any' to bypass the TypeScript error.
 */
const keycloakConfig = getKeycloakConfig();

// Cast to any to avoid TypeScript error, but use 'new' as required at runtime
const keycloak = new (Keycloak as any)({
  url: keycloakConfig.url,
  realm: keycloakConfig.realm,
  clientId: keycloakConfig.clientId,
});

export default keycloak;
